import { type ButtonHTMLAttributes, forwardRef } from 'react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'vote-yes' | 'vote-no' | 'skip' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
}

const variantStyles = {
  primary:
    'bg-gradient-to-r from-accent-light to-accent text-white hover:from-accent hover:to-accent-dark focus-visible:ring-accent',
  secondary:
    'bg-transparent text-black dark:text-white border border-gray-200 dark:border-gray-600 hover:border-accent hover:text-accent focus-visible:ring-accent',
  'vote-yes':
    'bg-vote-yes text-white hover:bg-vote-yes-hover focus-visible:ring-vote-yes',
  'vote-no':
    'bg-vote-no text-white hover:bg-vote-no-hover focus-visible:ring-vote-no',
  skip: 'bg-transparent text-gray-600 dark:text-gray-200 border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-900 focus-visible:ring-gray-400',
  ghost:
    'bg-transparent text-gray-600 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-900 hover:text-black dark:hover:text-white focus-visible:ring-gray-400',
};

const sizeStyles = {
  sm: 'px-3 py-1.5 text-sm min-h-touch',
  md: 'px-4 py-2 text-base min-h-touch-lg',
  lg: 'px-6 py-3 text-lg min-h-touch-lg min-w-[160px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      className = '',
      children,
      ...props
    },
    ref
  ) {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={`
          inline-flex items-center justify-center gap-2
          rounded-lg font-medium
          transition-all duration-150
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
          disabled:cursor-not-allowed disabled:opacity-50
          ${variantStyles[variant]}
          ${sizeStyles[size]}
          ${className}
        `}
        {...props}
      >
        {loading && (
          <span
            className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
            aria-hidden="true"
          />
        )}
        {children}
      </button>
    );
  }
);
